import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight, CheckCircle2, Building2, Home as HomeIcon, 
  Briefcase, FileText, MapPin, Target, TrendingUp, 
  Users, Shield, Clock, Upload, Phone, Mail
} from 'lucide-react';
import AnimatedSection, { 
  StaggerContainer, 
  StaggerItem 
} from '../components/AnimatedSection';
import { GlassSection } from '../components/GlassSection';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

const WeBuyNPNPage = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    email: '',
    phone: '',
    state: '',
    numberOfAssets: '',
    lienPosition: '',
    loanType: '',
    estimatedUPB: '',
    askingPrice: '',
    message: '',
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://formspree.io/f/meepwbqw', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          formType: 'We Buy NPN - Note Submission',
          fullName: formData.fullName,
          companyName: formData.companyName || 'Not provided',
          email: formData.email,
          phone: formData.phone || 'Not provided',
          state: formData.state || 'Not specified',
          numberOfAssets: formData.numberOfAssets || 'Not specified',
          lienPosition: formData.lienPosition || 'Not specified',
          loanType: formData.loanType || 'Not specified',
          estimatedUPB: formData.estimatedUPB || 'Not provided',
          askingPrice: formData.askingPrice || 'Not provided',
          message: formData.message || 'No additional message',
          hasFileAttachment: selectedFile ? `Yes - ${selectedFile.name}` : 'No file uploaded',
        })
      });

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        alert('Something went wrong. Please try again or call us directly.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Something went wrong. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Asset Types Data
  const assetTypes = [
    {
      icon: HomeIcon,
      title: 'Residential Notes',
      items: ['Single-family homes', 'Condominiums', 'Multi-family (2–4 units)'],
    },
    {
      icon: Building2,
      title: 'Commercial Notes',
      items: ['Office', 'Retail', 'Industrial', 'Mixed-use'],
    },
    {
      icon: FileText,
      title: 'Loan Types',
      items: ['Non-performing loans (NPN)', 'Sub-performing loans', 'Re-performing loans (selectively)', 'Lien Position: 2nd liens'],
    },
  ];

  // Geographic Focus Data
  const marketReasons = [
    'Strong real estate liquidity',
    'Active secondary note market',
    'Defined foreclosure frameworks',
    'Consistent investor demand',
  ];

  // Investment Criteria Data
  const investmentCriteria = [
    'Equity position',
    'Property location (FL / CA priority)',
    'Borrower profile',
    'Lien position',
    'Exit strategy',
  ];

  const preferredAssets = [
    'Assets with equity',
    'Owner-occupied (residential)',
    'Clear resolution path',
  ];

  // Strategy Data
  const strategyItems = [
    'Loan restructuring',
    'Discounted payoffs',
    'Borrower engagement',
    'Note resale strategies',
  ];

  // Why Work With Us Data
  const whyWorkWithUs = [
    { icon: Shield, text: 'Direct buyer — no assignment chains' },
    { icon: Clock, text: 'Fast underwriting (24–72 hours)' },
    { icon: Target, text: 'Clear and straightforward pricing' },
    { icon: MapPin, text: 'Experience with FL & CA markets' },
    { icon: Users, text: 'Professional and discreet process' },
  ];

  return (
    <div data-testid="we-buy-npn-page" className="overflow-hidden">
      {/* Hero Section */}
      <section 
        data-testid="npn-hero-section"
        className="relative min-h-[70vh] flex items-center pt-20"
      >
        {/* Background with Gradient Overlay */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-darkgreen via-darkgreen/95 to-emerald/90" />
          
          {/* Decorative geometric shapes */}
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-emerald/20 rounded-full blur-3xl animate-pulse-glow" />
          <div className="absolute bottom-1/4 left-1/4 w-72 h-72 bg-gold/10 rounded-full blur-3xl animate-pulse-glow animate-delay-200" />
          
          {/* Grid pattern overlay */}
          <div className="absolute inset-0 opacity-5">
            <div className="h-full w-full" style={{
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
              backgroundSize: '50px 50px'
            }} />
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <motion.span 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="inline-block bg-gold/20 text-gold font-subheading font-medium text-sm px-4 py-2 rounded-full mb-6 border border-gold/30 backdrop-blur-sm"
              >
                Mortgage Note Investments
              </motion.span>
              
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6">
                Mortgage Note Investments in{' '}
                <motion.span 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  className="text-gold"
                >
                  Florida & California
                </motion.span>
              </h1>
              
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="font-subheading text-lg md:text-xl text-white/85 mb-10 leading-relaxed max-w-2xl"
              >
                Green Palm Consulting focuses on acquiring mortgage notes across Florida and California, 
                including residential, commercial, and mixed-use assets. We specialize in distressed debt 
                opportunities where local market knowledge and execution speed matter.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.8 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <a
                  href="#submit-notes"
                  data-testid="hero-submit-notes-btn"
                  className="btn-premium inline-flex items-center justify-center bg-emerald hover:bg-emerald-dark text-white font-bold text-base px-8 py-4 rounded-full group"
                >
                  Submit Your FL / CA Notes
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </a>
                <a
                  href="#contact-form"
                  data-testid="hero-contact-btn"
                  className="inline-flex items-center justify-center bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white hover:bg-white hover:text-darkgreen font-bold text-base px-8 py-4 rounded-full transition-all duration-400 hover:scale-105"
                >
                  Contact Us
                </a>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Transition Wave */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="relative z-20 -mt-1"
      >
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" preserveAspectRatio="none">
          <path d="M0 60L60 52C120 44 240 28 360 24C480 20 600 28 720 32C840 36 960 36 1080 32C1200 28 1320 20 1380 16L1440 12V0H1380C1320 0 1200 0 1080 0C960 0 840 0 720 0C600 0 480 0 360 0C240 0 120 0 60 0H0V60Z" fill="#0A3D2D"/>
        </svg>
      </motion.div>

      {/* Section 1: What We Buy */}
      <GlassSection>
        <section 
          data-testid="what-we-buy-section"
          className="py-24 md:py-32 bg-gradient-section-alt relative overflow-hidden"
        >
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-20 right-20 w-80 h-80 bg-emerald/5 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-7xl mx-auto px-6 md:px-12">
            <AnimatedSection className="text-center mb-16" scale blur>
              <span className="inline-block bg-emerald/10 text-emerald font-subheading font-medium text-sm px-4 py-2 rounded-full mb-4">
                Asset Types
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-darkgreen mb-4">
                Asset Types We Acquire
              </h2>
            </AnimatedSection>

            <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-8" staggerDelay={0.15}>
              {assetTypes.map((type, index) => (
                <StaggerItem key={index}>
                  <motion.div 
                    data-testid={`asset-type-${index + 1}`}
                    className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 h-full card-premium"
                    whileHover={{ y: -8 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="w-14 h-14 bg-emerald/10 rounded-xl flex items-center justify-center mb-6">
                      <type.icon className="w-7 h-7 text-emerald" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-darkgreen mb-4">
                      {type.title}
                    </h3>
                    <ul className="space-y-3">
                      {type.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-emerald flex-shrink-0 mt-0.5" />
                          <span className="font-body text-gray-600">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </GlassSection>

      {/* Section 2: Geographic Focus */}
      <GlassSection darkSection>
        <section 
          data-testid="geographic-focus-section"
          className="py-24 md:py-32 relative overflow-hidden"
          style={{
            background: 'linear-gradient(180deg, #0A3D2D 0%, #062B1F 50%, #0A3D2D 100%)'
          }}
        >
          <div className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(ellipse 80% 50% at 20% 20%, rgba(4, 99, 7, 0.3) 0%, transparent 50%), radial-gradient(ellipse 60% 40% at 80% 80%, rgba(212, 175, 55, 0.1) 0%, transparent 50%)'
          }} />

          <div className="relative max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <AnimatedSection direction="left">
                <span className="inline-block bg-gold/20 text-gold font-subheading font-medium text-sm px-4 py-2 rounded-full mb-6 border border-gold/30">
                  Our Markets
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-6">
                  Focused Markets: Florida & California
                </h2>
                <p className="font-subheading text-base md:text-lg text-white/80 mb-8 leading-relaxed">
                  We concentrate on Florida and California due to:
                </p>
                <ul className="space-y-4 mb-8">
                  {marketReasons.map((reason, index) => (
                    <motion.li 
                      key={index}
                      className="flex items-center gap-3"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 + index * 0.1 }}
                    >
                      <div className="w-2 h-2 bg-gold rounded-full flex-shrink-0" />
                      <span className="font-body text-white/90">{reason}</span>
                    </motion.li>
                  ))}
                </ul>
                <p className="font-body text-white/70 text-sm italic">
                  Our local market focus allows us to move faster and price assets more accurately.
                </p>
              </AnimatedSection>

              <AnimatedSection direction="right" delay={0.2}>
                <div className="relative">
                  <motion.div 
                    className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 bg-gold/20 rounded-2xl flex items-center justify-center">
                        <MapPin className="w-8 h-8 text-gold" />
                      </div>
                      <div>
                        <p className="font-heading font-bold text-2xl text-white">FL & CA</p>
                        <p className="text-white/60 text-sm">Primary Markets</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/5 rounded-xl p-4">
                        <p className="font-heading font-bold text-3xl text-emerald">FL</p>
                        <p className="text-white/60 text-sm">Florida</p>
                      </div>
                      <div className="bg-white/5 rounded-xl p-4">
                        <p className="font-heading font-bold text-3xl text-emerald">CA</p>
                        <p className="text-white/60 text-sm">California</p>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </section>
      </GlassSection>

      {/* Section 3: Investment Criteria */}
      <GlassSection>
        <section 
          data-testid="investment-criteria-section"
          className="py-24 md:py-32 bg-gradient-section relative overflow-hidden"
        >
          <div className="relative max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <AnimatedSection direction="left">
                <span className="inline-block bg-emerald/10 text-emerald font-subheading font-medium text-sm px-4 py-2 rounded-full mb-6">
                  Evaluation Process
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-darkgreen mb-6">
                  How We Evaluate Deals
                </h2>
                <p className="font-subheading text-base md:text-lg text-gray-600 mb-8 leading-relaxed">
                  We analyze each note based on:
                </p>
                <ul className="space-y-3 mb-8">
                  {investmentCriteria.map((criteria, index) => (
                    <motion.li 
                      key={index}
                      className="flex items-center gap-3"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 + index * 0.08 }}
                    >
                      <CheckCircle2 className="w-5 h-5 text-emerald flex-shrink-0" />
                      <span className="font-body text-gray-700">{criteria}</span>
                    </motion.li>
                  ))}
                </ul>
              </AnimatedSection>

              <AnimatedSection direction="right" delay={0.2}>
                <motion.div 
                  className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gold/20 rounded-xl flex items-center justify-center">
                      <Target className="w-6 h-6 text-gold" />
                    </div>
                    <h3 className="font-heading font-bold text-xl text-darkgreen">Preferred</h3>
                  </div>
                  <ul className="space-y-4">
                    {preferredAssets.map((asset, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-emerald/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <CheckCircle2 className="w-4 h-4 text-emerald" />
                        </div>
                        <span className="font-body text-gray-700">{asset}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </AnimatedSection>
            </div>
          </div>
        </section>
      </GlassSection>

      {/* Section 4: Strategy */}
      <GlassSection>
        <section 
          data-testid="strategy-section"
          className="py-24 md:py-32 bg-gradient-section-alt relative overflow-hidden"
        >
          <div className="relative max-w-7xl mx-auto px-6 md:px-12">
            <AnimatedSection className="text-center mb-16" scale blur>
              <span className="inline-block bg-emerald/10 text-emerald font-subheading font-medium text-sm px-4 py-2 rounded-full mb-4">
                Our Approach
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-darkgreen mb-4">
                Our Approach to Value Creation
              </h2>
              <p className="font-subheading text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
                We focus on:
              </p>
            </AnimatedSection>

            <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" staggerDelay={0.1}>
              {strategyItems.map((item, index) => (
                <StaggerItem key={index}>
                  <motion.div 
                    data-testid={`strategy-item-${index + 1}`}
                    className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 text-center card-premium h-full"
                    whileHover={{ y: -5 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="w-12 h-12 bg-emerald/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <TrendingUp className="w-6 h-6 text-emerald" />
                    </div>
                    <p className="font-heading font-bold text-darkgreen">{item}</p>
                  </motion.div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </GlassSection>

      {/* Section 5: Why Work With Us */}
      <GlassSection darkSection>
        <section 
          data-testid="why-work-with-us-section"
          className="py-24 md:py-32 relative overflow-hidden"
          style={{
            background: 'linear-gradient(180deg, #0A3D2D 0%, #062B1F 50%, #0A3D2D 100%)'
          }}
        >
          <div className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(ellipse 60% 40% at 80% 20%, rgba(4, 99, 7, 0.2) 0%, transparent 50%)'
          }} />

          <div className="relative max-w-7xl mx-auto px-6 md:px-12">
            <AnimatedSection className="text-center mb-16" scale blur>
              <span className="inline-block bg-gold/20 text-gold font-subheading font-medium text-sm px-4 py-2 rounded-full mb-4 border border-gold/30">
                Our Advantage
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4">
                Why Work With Us
              </h2>
            </AnimatedSection>

            <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6" staggerDelay={0.1}>
              {whyWorkWithUs.map((item, index) => (
                <StaggerItem key={index}>
                  <motion.div 
                    data-testid={`why-us-item-${index + 1}`}
                    className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 text-center h-full"
                    whileHover={{ y: -5, backgroundColor: 'rgba(255,255,255,0.15)' }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="w-12 h-12 bg-gold/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <item.icon className="w-6 h-6 text-gold" />
                    </div>
                    <p className="font-body text-white/90 text-sm leading-relaxed">{item.text}</p>
                  </motion.div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </GlassSection>

      {/* Section 6: Partners */}
      <GlassSection>
        <section 
          data-testid="partners-section"
          className="py-16 md:py-20 bg-gradient-section-alt relative overflow-hidden"
        >
          <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
            <AnimatedSection scale blur>
              <motion.div 
                className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border border-gray-100"
                whileHover={{ scale: 1.01 }}
                transition={{ duration: 0.3 }}
              >
                <div className="w-16 h-16 bg-emerald/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Users className="w-8 h-8 text-emerald" />
                </div>
                <p className="font-subheading text-lg md:text-xl text-gray-700 leading-relaxed">
                  We actively work with sellers focused on Florida and California portfolios.
                </p>
              </motion.div>
            </AnimatedSection>
          </div>
        </section>
      </GlassSection>

      {/* Section 7: CTA */}
      <section 
        id="submit-notes"
        data-testid="npn-cta-section"
        className="py-24 md:py-32 bg-darkgreen relative overflow-hidden"
      >
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-emerald/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 animate-pulse-glow" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-gold/15 rounded-full blur-3xl translate-x-1/2 translate-y-1/2 animate-pulse-glow animate-delay-200" />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <AnimatedSection scale blur>
            <motion.h2 
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-6"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
            >
              Have Notes in Florida or California?
            </motion.h2>
            <motion.p 
              className="font-subheading text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
            >
              We are actively buying mortgage notes in FL and CA and reviewing new opportunities on an ongoing basis.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <a
                href="#contact-form"
                data-testid="cta-submit-notes-btn"
                className="btn-premium inline-flex items-center justify-center bg-emerald hover:bg-emerald-dark text-white font-bold text-lg px-10 py-5 rounded-full group"
              >
                Submit Your FL / CA Notes
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#contact-form"
                data-testid="cta-contact-btn"
                className="inline-flex items-center justify-center bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white hover:bg-white hover:text-darkgreen font-bold text-lg px-10 py-5 rounded-full transition-all duration-400 hover:scale-105"
              >
                Contact Us
              </a>
            </motion.div>
          </AnimatedSection>
        </div>
      </section>

      {/* Contact Form Section */}
      <section 
        id="contact-form"
        data-testid="npn-contact-form-section"
        className="py-24 md:py-32 bg-gradient-section-alt relative overflow-hidden"
      >
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <motion.div 
            className="absolute top-20 right-20 w-72 h-72 bg-emerald/10 rounded-full blur-3xl"
            animate={{ y: [0, -20, 0], scale: [1, 1.1, 1] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute bottom-20 left-20 w-72 h-72 bg-gold/10 rounded-full blur-3xl"
            animate={{ y: [0, 20, 0], scale: [1, 0.9, 1] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 md:px-12">
          <AnimatedSection className="text-center mb-12" scale blur>
            <span className="inline-block bg-emerald/10 text-emerald font-subheading font-medium text-sm px-4 py-2 rounded-full mb-4">
              Get Started
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-darkgreen mb-4">
              Submit Your Notes
            </h2>
          </AnimatedSection>

          <AnimatedSection direction="up" delay={0.2}>
            <motion.div 
              className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border border-gray-100 relative overflow-hidden"
              whileHover={{ boxShadow: '0 30px 60px rgba(10, 61, 45, 0.15)' }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-emerald/5 via-transparent to-gold/5 pointer-events-none" />
              
              <AnimatePresence mode="wait">
                {!isSubmitted ? (
                  <motion.div
                    key="form"
                    initial={{ opacity: 1 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="relative"
                  >
                    <form onSubmit={handleSubmit} className="space-y-8">
                      {/* Basic Info Section */}
                      <div>
                        <h3 className="font-heading font-bold text-lg text-darkgreen mb-6 flex items-center gap-2">
                          <Users className="w-5 h-5 text-emerald" />
                          Basic Info
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <Label htmlFor="fullName" className="text-darkgreen font-medium">
                              Full Name *
                            </Label>
                            <Input
                              id="fullName"
                              name="fullName"
                              type="text"
                              required
                              placeholder="John Smith"
                              value={formData.fullName}
                              onChange={handleChange}
                              data-testid="form-fullname-input"
                              className="h-14 rounded-xl border-gray-200 focus:border-emerald focus:ring-emerald bg-gray-50/50"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="companyName" className="text-darkgreen font-medium">
                              Company Name
                            </Label>
                            <Input
                              id="companyName"
                              name="companyName"
                              type="text"
                              placeholder="ABC Capital LLC"
                              value={formData.companyName}
                              onChange={handleChange}
                              data-testid="form-company-input"
                              className="h-14 rounded-xl border-gray-200 focus:border-emerald focus:ring-emerald bg-gray-50/50"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="email" className="text-darkgreen font-medium">
                              Email *
                            </Label>
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              required
                              placeholder="john@example.com"
                              value={formData.email}
                              onChange={handleChange}
                              data-testid="form-email-input"
                              className="h-14 rounded-xl border-gray-200 focus:border-emerald focus:ring-emerald bg-gray-50/50"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="phone" className="text-darkgreen font-medium">
                              Phone
                            </Label>
                            <Input
                              id="phone"
                              name="phone"
                              type="tel"
                              placeholder="(555) 123-4567"
                              value={formData.phone}
                              onChange={handleChange}
                              data-testid="form-phone-input"
                              className="h-14 rounded-xl border-gray-200 focus:border-emerald focus:ring-emerald bg-gray-50/50"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Asset Info Section */}
                      <div>
                        <h3 className="font-heading font-bold text-lg text-darkgreen mb-6 flex items-center gap-2">
                          <Briefcase className="w-5 h-5 text-emerald" />
                          Asset Info
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <Label htmlFor="state" className="text-darkgreen font-medium">
                              State
                            </Label>
                            <select
                              id="state"
                              name="state"
                              value={formData.state}
                              onChange={handleChange}
                              data-testid="form-state-select"
                              className="flex h-14 w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:border-emerald focus:outline-none focus:ring-2 focus:ring-emerald focus:ring-offset-2"
                            >
                              <option value="">Select State</option>
                              <option value="florida">Florida</option>
                              <option value="california">California</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="numberOfAssets" className="text-darkgreen font-medium">
                              Number of Assets
                            </Label>
                            <Input
                              id="numberOfAssets"
                              name="numberOfAssets"
                              type="number"
                              placeholder="e.g., 10"
                              value={formData.numberOfAssets}
                              onChange={handleChange}
                              data-testid="form-assets-input"
                              className="h-14 rounded-xl border-gray-200 focus:border-emerald focus:ring-emerald bg-gray-50/50"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="lienPosition" className="text-darkgreen font-medium">
                              Lien Position
                            </Label>
                            <select
                              id="lienPosition"
                              name="lienPosition"
                              value={formData.lienPosition}
                              onChange={handleChange}
                              data-testid="form-lien-select"
                              className="flex h-14 w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:border-emerald focus:outline-none focus:ring-2 focus:ring-emerald focus:ring-offset-2"
                            >
                              <option value="">Select Position</option>
                              <option value="1st">1st</option>
                              <option value="2nd">2nd</option>
                              <option value="mixed">Mixed</option>
                            </select>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="loanType" className="text-darkgreen font-medium">
                              Loan Type
                            </Label>
                            <select
                              id="loanType"
                              name="loanType"
                              value={formData.loanType}
                              onChange={handleChange}
                              data-testid="form-loantype-select"
                              className="flex h-14 w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:border-emerald focus:outline-none focus:ring-2 focus:ring-emerald focus:ring-offset-2"
                            >
                              <option value="">Select Type</option>
                              <option value="npn">NPN</option>
                              <option value="performing">Performing</option>
                              <option value="mixed">Mixed</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Deal Details Section */}
                      <div>
                        <h3 className="font-heading font-bold text-lg text-darkgreen mb-6 flex items-center gap-2">
                          <FileText className="w-5 h-5 text-emerald" />
                          Deal Details
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <Label htmlFor="estimatedUPB" className="text-darkgreen font-medium">
                              Estimated UPB
                            </Label>
                            <Input
                              id="estimatedUPB"
                              name="estimatedUPB"
                              type="text"
                              placeholder="$500,000"
                              value={formData.estimatedUPB}
                              onChange={handleChange}
                              data-testid="form-upb-input"
                              className="h-14 rounded-xl border-gray-200 focus:border-emerald focus:ring-emerald bg-gray-50/50"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="askingPrice" className="text-darkgreen font-medium">
                              Asking Price (optional)
                            </Label>
                            <Input
                              id="askingPrice"
                              name="askingPrice"
                              type="text"
                              placeholder="$350,000"
                              value={formData.askingPrice}
                              onChange={handleChange}
                              data-testid="form-asking-price-input"
                              className="h-14 rounded-xl border-gray-200 focus:border-emerald focus:ring-emerald bg-gray-50/50"
                            />
                          </div>
                        </div>

                        {/* File Upload */}
                        <div className="mt-6 space-y-2">
                          <Label htmlFor="loanTape" className="text-darkgreen font-medium">
                            Upload Loan Tape (Excel)
                          </Label>
                          <div className="relative">
                            <input
                              type="file"
                              id="loanTape"
                              accept=".xlsx,.xls,.csv"
                              onChange={handleFileChange}
                              data-testid="form-file-input"
                              className="hidden"
                            />
                            <label
                              htmlFor="loanTape"
                              className="flex items-center justify-center gap-3 h-14 w-full rounded-xl border-2 border-dashed border-gray-300 bg-gray-50/50 px-4 py-2 text-sm cursor-pointer hover:border-emerald hover:bg-emerald/5 transition-all duration-300"
                            >
                              <Upload className="w-5 h-5 text-gray-500" />
                              <span className="text-gray-600">
                                {selectedFile ? selectedFile.name : 'Click to upload Excel file'}
                              </span>
                            </label>
                          </div>
                        </div>

                        {/* Message */}
                        <div className="mt-6 space-y-2">
                          <Label htmlFor="message" className="text-darkgreen font-medium">
                            Message (optional)
                          </Label>
                          <Textarea
                            id="message"
                            name="message"
                            placeholder="Short description of the assets..."
                            value={formData.message}
                            onChange={handleChange}
                            data-testid="form-message-input"
                            className="min-h-[120px] rounded-xl border-gray-200 focus:border-emerald focus:ring-emerald bg-gray-50/50 resize-none"
                          />
                        </div>
                      </div>

                      {/* Submit Button */}
                      <motion.button
                        type="submit"
                        disabled={isSubmitting}
                        data-testid="form-submit-button"
                        className="btn-premium w-full bg-emerald hover:bg-emerald-dark text-white font-bold text-lg px-8 py-5 rounded-full disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {isSubmitting ? (
                          <>
                            <motion.svg 
                              className="h-5 w-5" 
                              viewBox="0 0 24 24"
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                                fill="none"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              />
                            </motion.svg>
                            Submitting...
                          </>
                        ) : (
                          <>
                            Submit for Review
                            <ArrowRight className="w-5 h-5" />
                          </>
                        )}
                      </motion.button>

                      <p className="text-center text-xs text-gray-500 mt-4">
                        By submitting, you agree to our{' '}
                        <a href="/privacy" className="text-emerald hover:underline">
                          Privacy Policy
                        </a>{' '}
                        and{' '}
                        <a href="/terms" className="text-emerald hover:underline">
                          Terms of Service
                        </a>
                      </p>
                    </form>
                  </motion.div>
                ) : (
                  /* Success State */
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-8 relative"
                    data-testid="form-success-message"
                  >
                    <motion.div 
                      className="w-20 h-20 bg-emerald/10 rounded-full flex items-center justify-center mx-auto mb-6"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
                    >
                      <CheckCircle2 className="w-10 h-10 text-emerald" />
                    </motion.div>
                    <h2 className="font-heading text-2xl font-bold text-darkgreen mb-4">
                      Thank You!
                    </h2>
                    <p className="font-body text-gray-600 mb-6">
                      We've received your submission. Our team will review your 
                      note details and contact you within 24–72 hours.
                    </p>
                    <div className="bg-gray-50 rounded-xl p-6">
                      <p className="text-sm text-gray-500 mb-2">What happens next?</p>
                      <ul className="text-left space-y-2">
                        {[
                          'Our team reviews your asset information',
                          'We conduct preliminary due diligence',
                          'We contact you with pricing and next steps',
                        ].map((item, index) => (
                          <motion.li 
                            key={index}
                            className="flex items-start gap-2 text-sm text-gray-700"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.4 + index * 0.1 }}
                          >
                            <CheckCircle2 className="w-4 h-4 text-emerald mt-0.5 flex-shrink-0" />
                            {item}
                          </motion.li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default WeBuyNPNPage;
